import React, { useState, useEffect } from 'react';
import { Menu, Spin } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  DollarOutlined,
  TeamOutlined,
  MessageOutlined,
  SettingOutlined,
  FireOutlined,
  ShopOutlined,
  PictureOutlined,
  SafetyOutlined,
  GiftOutlined,
  AccountBookOutlined,
  ShoppingCartOutlined,
  FileProtectOutlined,
  BellOutlined,
  BookOutlined,
  GlobalOutlined,
  MailOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import api from '../utils/api';

// 图标映射
const iconMap = {
  DashboardOutlined: <DashboardOutlined />,
  UserOutlined: <UserOutlined />,
  ShoppingOutlined: <ShoppingOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  DollarOutlined: <DollarOutlined />,
  AccountBookOutlined: <AccountBookOutlined />,
  TeamOutlined: <TeamOutlined />,
  MessageOutlined: <MessageOutlined />,
  SettingOutlined: <SettingOutlined />,
  FireOutlined: <FireOutlined />,
  GiftOutlined: <GiftOutlined />,
  ShopOutlined: <ShopOutlined />,
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  PictureOutlined: <PictureOutlined />,
  SafetyOutlined: <SafetyOutlined />,
  FileProtectOutlined: <FileProtectOutlined />,
  BellOutlined: <BellOutlined />,
  BookOutlined: <BookOutlined />,
  GlobalOutlined: <GlobalOutlined />,
  MailOutlined: <MailOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
};

const DynamicMenu = ({ onMenuLoad }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKeys, setOpenKeys] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/menus/current');
      const { menuTree, permissions, role } = response.data;

      // 转换菜单数据为 Ant Design Menu 格式
      const formattedMenus = convertMenus(menuTree);
      setMenuItems(formattedMenus);

      // 默认展开当前路径的父菜单
      const defaultOpenKeys = getDefaultOpenKeys(formattedMenus, location.pathname);
      setOpenKeys(defaultOpenKeys);

      // 通知父组件菜单加载完成
      if (onMenuLoad) {
        onMenuLoad({ menuTree, permissions, role });
      }
    } catch (error) {
      console.error('获取菜单失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 转换菜单数据格式
  const convertMenus = (menus) => {
    if (!menus || menus.length === 0) return [];

    return menus.map(menu => {
      const item = {
        key: menu.route_path || menu.id.toString(),
        label: menu.menu_name,
        icon: menu.icon ? iconMap[menu.icon] : null,
      };

      // 如果有子菜单，递归转换
      if (menu.children && menu.children.length > 0) {
        item.children = convertMenus(menu.children);
      }

      return item;
    });
  };

  // 获取默认展开的菜单键
  const getDefaultOpenKeys = (menus, pathname) => {
    const keys = [];
    
    const findParentKeys = (items, path, parentKeys = []) => {
      for (const item of items) {
        if (item.key === path) {
          keys.push(...parentKeys);
          return true;
        }
        
        if (item.children) {
          const found = findParentKeys(item.children, path, [...parentKeys, item.key]);
          if (found) return true;
        }
      }
      return false;
    };

    findParentKeys(menus, pathname);
    return keys;
  };

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <Spin tip="加载菜单中..." />
      </div>
    );
  }

  return (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      openKeys={openKeys}
      items={menuItems}
      onClick={handleMenuClick}
      onOpenChange={handleOpenChange}
      style={{
        border: 'none',
        fontSize: '14px',
        height: 'calc(100vh - 64px - 16px)',
        overflow: 'auto',
      }}
    />
  );
};

export default DynamicMenu;
